import apiClient from './client';

export const EXPENSE_CATEGORIES = [
  'materials', 'tools', 'equipment_rental', 'fuel', 'subcontractor',
  'permits', 'disposal', 'meals', 'lodging', 'office', 'other',
] as const;
export type ExpenseCategory = typeof EXPENSE_CATEGORIES[number];

export interface ExpenseLineItem {
  description: string;
  sku?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  amount: number | null;
}

export interface PriceMatch {
  line_description: string;
  quantity: number | null;
  receipt_unit_price: number;
  product_id: number;
  product_name: string;
  product_sku: string | null;
  unit_of_measure: string | null;
  current_price: number | null;
  match_type: 'sku' | 'name';
  confidence: number;
  price_changed: boolean;
}

export interface PriceUpdate {
  product_id: number;
  unit_price: number;
  expense_id?: number;
}

export interface JobExpense {
  vendor_id?: number | null;
  paid?: number | boolean;
  due_date?: string | null;
  invoice_number?: string | null;
  id: number;
  job_id: number;
  document_id: number | null;
  vendor: string | null;
  category: string;
  expense_date: string | null;
  subtotal: number | null;
  tax: number | null;
  total: number;
  currency: string;
  description: string | null;
  line_items: ExpenseLineItem[];
  source: 'receipt_scan' | 'manual';
  ai_analyzed: boolean;
  receipt_url: string | null;
  receipt_name: string | null;
  created_at: string;
  /** Billed through to a client invoice (#210) */
  invoiced_on_invoice_id?: number | null;
  billed_amount?: number | null;
  billed_invoice_number?: string | null;
}

export interface ExpenseSummary {
  total: number;
  count: number;
  byCategory: Record<string, number>;
}

export const getJobExpenses = async (
  jobId: string | number,
): Promise<{ expenses: JobExpense[]; summary: ExpenseSummary }> => {
  const { data } = await apiClient.get(`/jobs/${jobId}/expenses`);
  return data.data;
};

// Upload a receipt image/PDF; the server analyzes it and returns the created expense.
export const scanReceipt = async (
  jobId: string | number,
  file: File,
): Promise<{ expense: JobExpense; warning?: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post(`/jobs/${jobId}/expenses/scan`, formData, {
    transformRequest: [(data, headers) => {
      headers.delete('Content-Type'); // let the browser set the multipart boundary
      return data;
    }],
    timeout: 180000, // AI analysis can take a while
  });
  return { expense: res.data.data, warning: res.data.warning };
};

export interface ExpenseInput {
  vendor?: string | null;
  vendor_id?: number | null;
  category?: string;
  expense_date?: string | null;
  subtotal?: number | null;
  tax?: number | null;
  total: number;
  description?: string | null;
  paid?: boolean;
  due_date?: string | null;
  invoice_number?: string | null;
}

export const createExpense = async (
  jobId: string | number,
  payload: ExpenseInput,
): Promise<JobExpense> => {
  const { data } = await apiClient.post(`/jobs/${jobId}/expenses`, payload);
  return data.data;
};

export const updateExpense = async (
  jobId: string | number,
  expenseId: number,
  payload: Partial<ExpenseInput>,
): Promise<JobExpense> => {
  const { data } = await apiClient.put(`/jobs/${jobId}/expenses/${expenseId}`, payload);
  return data.data;
};

export const deleteExpense = async (
  jobId: string | number,
  expenseId: number,
): Promise<void> => {
  await apiClient.delete(`/jobs/${jobId}/expenses/${expenseId}`);
};

// Propose catalog price updates from a scanned receipt (review-only).
export const getPriceMatches = async (
  jobId: string | number,
  expenseId: number,
): Promise<PriceMatch[]> => {
  const { data } = await apiClient.get(`/jobs/${jobId}/expenses/${expenseId}/price-matches`);
  return data.data.matches;
};

// Apply the reviewed price updates to the catalog (records price history).
export const applyPriceUpdates = async (updates: PriceUpdate[]): Promise<{ applied: number }> => {
  const { data } = await apiClient.post('/catalog/products/price-updates', { updates });
  return { applied: data.applied };
};
